// src/data/services/jasa-web-development.tsx

"use client";
import { ServiceSection } from "@/components/ServiceSection";
import { site } from "@/data/default";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import Image from "next/image";

// Import Swiper styles
import "swiper/css/pagination";
import "swiper/css";

const JasaWebDevelopmentContent = (
  <>
    {/* DESKRIPSI */}
    <ServiceSection id="deskripsi" title="Jasa Web Development Profesional">
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
            Web Development <span className="text-purple">Custom, Scalable, & Berbasis Growth</span>
          </h1>
          <p className="text-base mb-4">
            Kami bangun website dan web app berbasis kebutuhanmu: mulai dari <strong>Odoo, Laravel, PHP + MySQL</strong> untuk corporate site yang stabil, hingga <strong>React & Next & Vue</strong> untuk startup yang butuh performa tinggi. Mau custom CMS? Mau API gateway? Mau dashboard yang bisa scaling ke ratusan ribu user? Semua bisa.
          </p>

          <ul className="text-sm space-y-2 mb-6 text-muted-foreground">
            <li>✅ Teknologi lengkap: Odoo, Laravel, PHP, React, NodeJs</li>
            <li>✅ Support frontend, backend, & fullstack (REST & GraphQL)</li>
            <li>✅ Bisa custom CMS, API, PWA, ERP, CRM, dashboard bisnis</li>
            <li>✅ Akses penuh source code, CI/CD, & deploy ke servermu</li>
          </ul>

          <a
            href={`https://wa.me/${site.whatsapp}?text=Halo%20${site.name}%2C%20saya%20ingin%20konsultasi%20tentang%20jasa%20web%20development`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-purple-600 to-orange-500 text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition"
          >
            🚀 Konsultasi Web Gratis Sekarang
          </a>
        </div>
        <div className="hidden md:block rounded-xl overflow-hidden">
          <Image
            src="/images/services/software-development.webp"
            alt="Ilustrasi Jasa Web Development Jakarta"
            className="w-full max-w-md mx-auto"
            width={1024}
            height={1024}
          />
        </div>
      </div>
    </ServiceSection>


    {/* KEUNGGULAN */}
    <ServiceSection id="keunggulan" title="Kenapa Jasa Web Development Kami Unggul?">
      <div className="grid md:grid-cols-2 gap-6 mt-10">
        {[
          {
            title: "🎯 Fokus pada Konversi",
            desc: "Setiap web yang kami buat diarahkan pada hasil bisnis: lebih banyak leads, pelanggan, atau penjualan.",
          },
          {
            title: "⚙️ Teknologi Terkini",
            desc: "Next.js, Tailwind, API-first, Headless CMS, kami pakai teknologi terbaik untuk kecepatan dan fleksibilitas maksimal.",
          },
          {
            title: "🧠 UX Berbasis Psikologi",
            desc: "Desain bukan cuma estetika. Kami pakai prinsip *neuromarketing* & behavioral funnel untuk mengarahkan pengunjung ke aksi.",
          },
          {
            title: "🔐 Akses & Kontrol Penuh",
            desc: "Source code, CMS, hingga DNS, semua milikmu. Tak ada kunci² tersembunyi. Kami bantu kamu paham dan berdaulat atas sistemmu sendiri.",
          },
        ].map((item, i) => (
          <div
            key={i}
            className="group flex flex-col justify-between p-6 border rounded-xl shadow-md bg-gradient-to-br from-white to-gray-50 hover:from-purple-50 hover:to-white transition-all duration-300 hover:shadow-xl"
          >
            <div className="flex items-start gap-4">
              <div className="text-4xl">{item.title.split(" ")[0]}</div>
              <div>
                <h3 className="text-lg font-semibold  mb-1">
                  {item.title.split(" ").slice(1).join(" ")}
                </h3>
                <p className="text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </ServiceSection>

    {/* LAYANAN */}
    <ServiceSection id="layanan" title="Layanan Jasa Web Development">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {[
          {
            title: "Website Perusahaan (PHP + Laravel)",
            tech: ["Laravel", "MySQL", "Blade", "cPanel"],
            desc: "Cocok untuk company profile, website formal, dan sistem internal perusahaan berbasis PHP yang ringan dan stabil.",
          },
          {
            title: "Web App React & Next.js",
            tech: ["React", "Next.js", "Tailwind", "Vercel"],
            desc: "Cocok untuk platform startup, dashboard modern, atau web interaktif yang butuh performa tinggi dan SEO maksimal.",
          },
          {
            title: "Fullstack Node.js + Express",
            tech: ["Node.js", "Express", "MongoDB", "REST API"],
            desc: "Ideal untuk backend yang powerful dengan load tinggi, API gateway, atau sistem login multi-role.",
          },
          {
            title: "Custom CMS & Dashboard",
            tech: ["Strapi", "Sanity", "Firebase", "Supabase"],
            desc: "Kami bangun CMS custom sesuai kebutuhanmu, bisa offline-first, role-based, dan realtime.",
          },
          {
            title: "Progressive Web App (PWA)",
            tech: ["Atom.js", "Service Worker", "Offline Cache"],
            desc: "Web yang berperilaku seperti app. Akses cepat, ringan, dan bisa digunakan offline, cocok untuk UMKM dan edukasi.",
          },
          {
            title: "Deployment & Maintenance",
            tech: ["VPS", "Docker", "CI/CD", "GitHub Action"],
            desc: "Tak perlu pusing server. Kami bantu deploy, integrasi domain, auto backup, dan performa 24/7.",
          },
        ].map((item, i) => (
          <article
            key={i}
            className="relative group p-6 bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 ease-in-out h-full flex flex-col justify-between"
            itemScope
            itemType="https://schema.org/Service"
          >
            <div>
              <h3 itemProp="name" className="text-xl font-bold  group-hover:text-purple-600 transition-colors">
                {item.title}
              </h3>
              <div className="flex flex-wrap gap-2 mt-3 mb-4">
                {item.tech.map((tech, t) => (
                  <span
                    key={t}
                    className="text-xs font-medium bg-purple-100 text-purple-800 px-2 py-1 rounded-md"
                  >
                    {tech}
                  </span>
                ))}
              </div>
              <p itemProp="description" className="text-sm leading-relaxed">
                {item.desc}
              </p>
            </div>

            <meta itemProp="provider" content="GrasiTech" />
            <meta itemProp="areaServed" content="Indonesia" />
          </article>
        ))}
      </div>
    </ServiceSection>

    {/* STUDI KASUS */}
    <ServiceSection id="studi-kasus" title="Studi Kasus Nyata - Web Development">
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {[
          {
            title: "Website E-commerce Herbal Custom",
            client: "Brand Lokal Herbal Alami",
            tech: ["Laravel", "MySQL", "WhatsApp Button"],
            result: "Transaksi naik 2.5x lipat setelah UI baru & tombol beli via WA. Bounce rate turun drastis dari 70% ke 28%.",
          },
          {
            title: "Platform Booking Lapangan Futsal",
            client: "Startup Olahraga Jakarta",
            tech: ["Next.js", "Calendar API", "Midtrans"],
            result: "Reservasi naik 300%. Sekarang pelanggan bisa booking & bayar dalam 2 klik dari HP.",
          },
          {
            title: "Dashboard Monitoring Proyek Konstruksi",
            client: "Perusahaan EPC Nasional",
            tech: ["React", "Firebase", "Realtime Sync"],
            result: "Laporan harian dari 12 site langsung otomatis via dashboard. Semua data live tanpa Google Sheet lagi.",
          },
          {
            title: "Sistem CMS Website Pemerintah",
            client: "Instansi Pemerintah Daerah",
            tech: ["PHP", "Custom CMS", "Role-Based Access"],
            result: "Website bisa dikelola langsung oleh 5 divisi, tanpa perlu kontak vendor tiap kali update konten.",
          },
        ].map((item, i) => (
          <div key={i} className="p-5 border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition duration-200">
            <h3 className="text-lg font-semibold mb-1">{item.title}</h3>
            <p className="text-sm text-muted-foreground italic mb-2">{item.client}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {item.tech.map((tech, t) => (
                <span key={t} className="text-xs bg-gray-100 px-2 py-0.5 rounded-md font-medium">
                  {tech}
                </span>
              ))}
            </div>
            <p className="text-sm">{item.result}</p>
          </div>
        ))}
      </div>
    </ServiceSection>

    {/* HARGA */}
    <ServiceSection id="harga" title="Harga Transparan & Bisa Disesuaikan">
      <div className="grid md:grid-cols-3 gap-4 my-10">
        {/* Starter */}
        <div className="flex flex-col justify-between border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all duration-300 bg-white">
          <div>
            <h3 className="font-bold mb-2 flex items-center gap-2">🌱 Paket Starter</h3>
            <p className="text-3xl font-bold mb-4">Rp5 Juta~</p>
            <ul className="text-sm space-y-2 mb-4">
              <li>✅ Company profile HTML atau PHP statis</li>
              <li>✅ 4 halaman + 1 kontak</li>
              <li>✅ Desain responsif</li>
              <li>✅ Estimasi 3-7 hari</li>
            </ul>
          </div>
          <p className="text-xs italic text-muted-foreground mt-6">
            Cocok untuk portfolio, UMKM, atau landing page event.
          </p>
        </div>

        {/* Bisnis */}
        <div className="flex flex-col justify-between relative border-2 border-purple-600 rounded-3xl p-6 bg-gradient-to-b from-purple-50 to-white shadow-lg hover:shadow-2xl transition-all duration-300 scale-[1.03] z-10">
          <div className="absolute -top-3 right-4 bg-purple-600 text-white text-xs px-3 py-1 rounded-full shadow">
            ⭐ Best Value
          </div>
          <div>
            <h3 className="font-bold mb-2 flex items-center gap-2 text-purple-700">🚀 Paket Bisnis</h3>
            <p className="text-3xl font-bold text-purple-800 mb-4">Rp12-25 Juta</p>
            <ul className="text-sm space-y-2 mb-4 ">
              <li>✅ Web app, dashboard, atau booking online</li>
              <li>✅ API integrasi: WA, Midtrans, Email</li>
              <li>✅ CMS custom + akses backend</li>
              <li>✅ Estimasi 2-6 minggu</li>
            </ul>
          </div>
          <p className="text-xs italic text-muted-foreground mt-6">
            Ideal untuk bisnis yang ingin skalabilitas & kontrol penuh.
          </p>
        </div>

        {/* Enterprise */}
        <div className="flex flex-col justify-between border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:scale-[1.02] transition-all duration-300 bg-white">
          <div>
            <h3 className="font-bold mb-2 flex items-center gap-2">🏢 Paket Enterprise</h3>
            <p className="text-3xl font-bold text-orange-600 mb-4">Rp50 Juta+</p>
            <ul className="text-sm space-y-2 mb-4">
              <li>✅ Sistem kompleks, ERP, CRM, multi-role</li>
              <li>✅ Fullstack modern (Laravel, React, API, CI/CD)</li>
              <li>✅ Dukungan scaling, audit, dan training</li>
              <li>✅ Estimasi fleksibel (6-24 minggu)</li>
            </ul>
          </div>
          <p className="text-xs italic text-muted-foreground mt-6">
            Cocok untuk korporasi skala nasional atau digital product.
          </p>
        </div>
      </div>
      <div className="text-center mt-10">
        <p className="text-sm mt-2 italic">
          Siap bertransformasi ke digital lewat website yang benar² dirancang untuk bertumbuh? Kami bantu kamu dari ide sampai deployment.
        </p>
        <p className="text-sm mb-2">Masih bingung paket mana yang cocok?</p>
        <a
          href={`https://wa.me/${site.whatsapp}?text=Halo%20${site.name}%2C%20saya%20mau%20diskusi%20soal%20jasa%20web%20development`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-gradient-to-r from-indigo-600 via-purple-600 to-orange-500 text-white text-sm px-6 py-3 rounded-full shadow-lg hover:scale-105 transition"
        >
          🚀 Kirim Ide Website Sekarang → Kami Bantu Breakdown
        </a>
      </div>
    </ServiceSection>

    {/* FAQ */}
    <ServiceSection id="faq" title="FAQ - Pertanyaan Seputar Jasa Web Development">
      <div className="space-y-4">
        {[
          {
            question: "Apakah bisa request fitur website yang sangat spesifik?",
            answer:
              "Tentu. Justru itu kekuatan utama kami sebagai tim jasa web development: membuat fitur custom dari nol, bukan template pasaran. Mulai dari sistem booking, dashboard analitik, membership login, sampai integrasi payment dan API pihak ketiga.",
          },
          {
            question: "Berapa lama waktu pengerjaan website?",
            answer:
              "Durasi tergantung tingkat kompleksitas. Website company profile: 3-7 hari. Web app skala bisnis: 2-6 minggu. Sistem enterprise: 6-24 minggu. Estimasi akurat akan kami berikan setelah konsultasi awal.",
          },
          {
            question: "Apakah saya mendapat akses penuh ke source code dan hosting?",
            answer:
              "Ya, 100%. Semua file, database, dan source code jadi milikmu sepenuhnya. Kami tidak melakukan vendor lock-in atau menyembunyikan akses teknis apa pun.",
          },
          {
            question: "Apakah bisa di-maintain atau di-scale setelah selesai?",
            answer:
              "Bisa. Website yang kami bangun punya arsitektur modular, jadi kamu bisa tambahkan fitur, scale jumlah user, atau ubah desain tanpa mulai dari awal. Tersedia juga layanan maintenance berlangganan jika dibutuhkan.",
          },
          {
            question: "Apakah saya bisa konsultasi dulu sebelum memutuskan?",
            answer:
              "Sangat disarankan. Konsultasi awal bersifat gratis tanpa komitmen. Kami bantu breakdown kebutuhanmu, merekomendasikan teknologi paling efisien, dan memberi estimasi harga yang realistis.",
          },
        ].map((item, i) => (
          <details
            key={i}
            className="group border border-gray-200 rounded-xl p-4 transition-all open:shadow-sm open:bg-gray-50"
          >
            <summary className="font-semibold text-base cursor-pointer list-none flex justify-between items-center">
              <span>{item.question}</span>
              <span className="ml-2 transform group-open:rotate-180 transition-transform">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708" />
                </svg>
              </span>
            </summary>
            <p className="mt-3 text-sm">{item.answer}</p>
          </details>
        ))}
      </div>

    </ServiceSection>

    {/* CLIENT LOGO */}
    <ServiceSection id="client-logo" title="Dipercaya oleh Klien dari Berbagai Industri">
      <div className="mt-6">
        <Swiper
          spaceBetween={20}
          slidesPerView={2}
          breakpoints={{
            640: { slidesPerView: 3 },
            768: { slidesPerView: 4 },
            1024: { slidesPerView: 5 },
          }}
          loop
          autoplay={{ delay: 3000 }}
          className="opacity-80 grayscale hover:grayscale-0 transition-all"
        >
          {[
            "/images/clients/client-1.png",
            "/images/clients/client-2.png",
            "/images/clients/client-3.png",
            "/images/clients/client-4.png",
            "/images/clients/client-5.png",
            "/images/clients/client-6.png",
            "/images/clients/client-7.png",
            "/images/clients/client-8.png",
          ].map((logo, index) => (
            <SwiperSlide key={index}>
              <div className="flex justify-center items-center h-20">
                <img
                  src={logo}
                  alt={`Logo Klien Web Dev ${index + 1}`}
                  className="max-h-12 object-contain"
                  loading="lazy"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </ServiceSection>

    {/* TESTIMONI */}
    <ServiceSection id="testimoni" title="Apa Kata Klien Kami?">
      <div className="">
        <Swiper
          spaceBetween={20}
          slidesPerView={1.2}
          breakpoints={{
            768: { slidesPerView: 2.2 },
            1024: { slidesPerView: 3 },
          }}
          autoplay={{ delay: 6000 }}
          loop
        >
          {[
            {
              name: "Rizky Aulia",
              job: "Owner Brand Skincare Lokal",
              comment:
                "Website saya sekarang kelihatan premium banget. Tim ini bantu dari copywriting sampai checkout flow. Penjualan naik drastis dalam 3 minggu pertama!",
              image: "/images/testimonials/testimony-rizky.webp",
            },
            {
              name: "Arif Wijaya",
              job: "Dosen & Konsultan Pendidikan",
              comment:
                "Saya minta web e-learning yang ringan, bisa diakses kapan pun. Hasilnya? Karyawan bisa belajar sendiri, sertifikat langsung generate otomatis.",
              image: "/images/testimonials/testimony-arif.webp",
            },
            {
              name: "Maya Putri",
              job: "Event Organizer & Freelancer",
              comment:
                "Dari awal saya nggak ngerti istilah teknis. Tapi mereka sabar banget menjelaskan step-by-step. Dan hasilnya: sistem booking venue saya jalan otomatis!",
              image: "/images/testimonials/testimony-maya.webp",
            },
            {
              name: "Budi Santoso",
              job: "Manager IT Perusahaan Konstruksi",
              comment:
                "Dashboard progres proyek yang mereka bangun bikin kerja saya jauh lebih cepat. Semua divisi bisa akses real-time tanpa Google Sheet lagi.",
              image: "/images/testimonials/testimony-budi.webp",
            },
          ].map((item, i) => (
            <SwiperSlide key={i} className="h-full flex self-stretch mb-4">
              <div className="bg-white border rounded-xl shadow-sm p-5 flex flex-col justify-between hover:shadow-md transition-all">
                <p className="text-sm leading-relaxed italic mb-4">"{item.comment}"</p>
                <div className="flex items-center gap-3">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-semibold">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.job}</p>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </ServiceSection>

    {/* KONTAK */}
    <ServiceSection id="kontak" title="Diskusi Proyek Web Development">
      <div className="grid md:grid-cols-2 gap-4 items-center mt-6">
        {/* Kiri: Copy Persuasif */}
        <div>
          <h3 className="text-2xl font-bold mb-4">
            Punya ide website, tapi bingung harus mulai dari mana?
          </h3>
          <p className="text-base mb-4">
            Ceritakan saja masalah atau visi digitalmu, kami bantu rumuskan solusinya secara teknis & strategis. Tanpa biaya, tanpa komitmen.
          </p>
          <ul className="text-sm space-y-1 mb-6 text-muted-foreground">
            <li>✅ Gratis konsultasi via WhatsApp langsung</li>
            <li>✅ Rekomendasi teknologi paling efisien untuk proyekmu</li>
            <li>✅ Breakdown kebutuhan → roadmap eksekusi</li>
            <li>✅ Estimasi waktu dan biaya akurat sejak awal</li>
          </ul>
          <a
            href={`https://wa.me/${site.whatsapp}?text=Halo%20${site.name}%2C%20saya%20butuh%20bantuan%20buat%20website%20atau%20web%20app`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-green text-white px-6 py-3 rounded-xl font-semibold hover:bg-green/90 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="text-white me-2" viewBox="0 0 16 16">
              <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
            </svg>
            Konsultasi Sekarang
          </a>
          <p className="text-xs mt-3">Kami aktif setiap hari, 09:00-22:00 WIB</p>
        </div>

        {/* Kanan: Google Maps Embed */}
        <div className="relative w-full h-64 md:h-full rounded-xl overflow-hidden">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3965.8420521856533!2d106.99524857499078!3d-6.284483493704425!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e698d6d29d17095%3A0x765e1cdf4db9b9bb!2sGratia%20Global%20Solution!5e0!3m2!1sid!2sid!4v1754466163988!5m2!1sid!2sid"
            width="100%"
            height="100%"
            allowFullScreen
            loading="lazy"
            className="border-0 w-full h-full"
            title="Lokasi Kantor"
          ></iframe>
          <div className="absolute bottom-4 left-4 bg-white/90 text-sm px-4 py-2 rounded shadow">
            📍 Berbasis di Bekasi - Jakarta, melayani seluruh Indonesia & luar negeri
          </div>
        </div>
      </div>
    </ServiceSection>

  </>
);

export default JasaWebDevelopmentContent;